import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { isAttachmentStrategyUsingCheckoutButtons } from "@redotech/redo-model/returns/attachment-strategy";
import { Treatment } from "@redotech/redo-model/split";
import {
  MERCHANDISE_OUT_OF_STOCK_CODE,
  REDO_LOADED_ON_CART_ATTRIBUTE_KEY,
  REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY,
} from "@redotech/redo-model/storefront/cart-attributes";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import {
  REDO_CART_STORAGE_KEY,
  REDO_CART_TOKEN_STORAGE_KEY,
} from "@redotech/redo-storefront-common/cache/keys";
import {
  getSessionStorageWithExpiry,
  setSessionStorageWithExpiry,
} from "@redotech/redo-web/utils/session-storage-wrapper";
import {
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartAttributes,
  ShopifyCartItem,
} from "@redotech/shopify-client/ajax";
import { httpResponseEvents } from "@redotech/web-util/http";
import isEqual from "lodash/isEqual";
import { useEffect, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { StorefrontClient } from "../client/storefront";
import { log } from "../monitoring/log";
import { ExtensionShopifyClientContext } from "../shopify";
import { useBFCacheListener } from "./checkout-buttons/on-page-refresh-hooks";
import { useJqueryEventListener } from "./jquery-events";

export const CART_UPDATE_JQUERY_EVENTS = [
  "cart:update",
  "product:afterAddToCart",
];

const isShopifyCartUrl = (url: string) =>
  /\/cart(\/|$)/i.test(url) &&
  !url.includes("api.getredo.com") &&
  !url.includes("localhost");

const isCartAdd = (body: string | undefined | null) => {
  if (!body) {
    return false;
  }

  const found = /cartLinesAdd/.test(body);
  return found;
};

export function useBuyerIdentity(
  shopifyStorefrontGraphqlClient?: StorefrontClient,
) {
  const buyerIdentity = useLoad(
    async (signal) => {
      try {
        return await shopifyStorefrontGraphqlClient?.getBuyerIdentity(signal);
      } catch (e) {
        return undefined;
      }
    },
    [shopifyStorefrontGraphqlClient],
  );

  return buyerIdentity;
}

export function useCart(manualRefresh?: symbol) {
  const [cart, setCart] = useState<ShopifyCart | undefined>(undefined);
  const storefrontClient = useRequiredContext(ExtensionShopifyClientContext);
  const [recheckCartWithDebounceSignal, setRecheckCartWithDebounceSignal] =
    useState<number>(0);
  const refreshCartNow = useDebounce(recheckCartWithDebounceSignal, 100);
  const avoidUnecessaryCartRequests =
    globalThis.redoStorefront?.type === "commercecloud"; // FIXME: Use for everyone after BFCM25

  const triggerCartRefreshWithDebounce = useHandler(() => {
    setRecheckCartWithDebounceSignal(recheckCartWithDebounceSignal + 1);
  });

  useEffect(() => {
    if (manualRefresh) {
      triggerCartRefreshWithDebounce();
    }
  }, [manualRefresh, triggerCartRefreshWithDebounce]);

  useEffect(
    () => {
      if (!cart) {
        const storageCart = getSessionStorageWithExpiry(REDO_CART_STORAGE_KEY);
        if (storageCart) {
          setCart(storageCart);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useJqueryEventListener(
    document.body,
    CART_UPDATE_JQUERY_EVENTS,
    useHandler(() => {
      log("Cart update detected from jQuery event listener");
      triggerCartRefreshWithDebounce();
    }),
  );

  useEffect(() => {
    if (
      typeof Shopify === "undefined" &&
      globalThis.Tapcart?.registerEventHandler
    ) {
      Tapcart!.registerEventHandler("cart/updated", function () {
        triggerCartRefreshWithDebounce();
      });
      return;
    } else {
      const subscription = httpResponseEvents.subscribe(({ request }) => {
        if (isShopifyCartUrl(request.url) || isCartAdd(request.body)) {
          triggerCartRefreshWithDebounce();
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [triggerCartRefreshWithDebounce]);

  useBFCacheListener(
    useHandler(() => {
      log("Page restored from bfcache, refreshing cart");
      triggerCartRefreshWithDebounce();
    }),
  );

  useLoad(
    async (signal) => {
      if (
        avoidUnecessaryCartRequests &&
        refreshCartNow === 0 &&
        typeof manualRefresh === "symbol"
      ) {
        // Prevent firing twice on initial load if manualRefresh is a symbol
        log(
          "Skipping cart check on initial load since expecting manual refresh, will check on manual refresh signal",
        );
        return;
      }
      for (let i = 0; i < 2; i++) {
        log("fetching cart");
        const newCart = await storefrontClient.cartGet(signal);
        if (!cart || !isEqual(cart, newCart)) {
          log("found new cart", { newCart });
          setCart(newCart);
          setSessionStorageWithExpiry(REDO_CART_STORAGE_KEY, newCart, 0.15);
          const oldToken = getSessionStorageWithExpiry(
            REDO_CART_TOKEN_STORAGE_KEY,
          );
          if (
            newCart.token &&
            newCart.token !== oldToken &&
            newCart.token.includes("?")
          ) {
            setSessionStorageWithExpiry(
              REDO_CART_TOKEN_STORAGE_KEY,
              newCart.token,
              0.15,
            );
          }
          if (newCart.items.length) {
            break;
          }
        } else if (
          avoidUnecessaryCartRequests &&
          cart &&
          (refreshCartNow === 0 ||
            (refreshCartNow == 1 && typeof manualRefresh === "symbol"))
        ) {
          // If this is our initial load of the cart, don't be concerned if the cart has not changed
          log("cart unchanged on initial load", { cart: newCart });
          break;
        } else {
          log("cart unchanged", { cart: newCart });
        }
      }
    },
    [refreshCartNow, triggerCartRefreshWithDebounce],
  );
  return cart;
}

export async function safeCartAdd({
  client,
  items,
  params,
  retries = 0,
}: {
  client: ExtensionShopifyClient;
  items: { items: Partial<ShopifyCartItem>[] };
  params?: URLSearchParams;
  retries?: number;
}): Promise<{ success: boolean; error?: Error }> {
  let attempts = 0;

  while (attempts <= retries) {
    try {
      await client.cartAdd(items, params, true);
      return { success: true };
    } catch (e) {
      if (
        e instanceof Error &&
        e.message.includes(MERCHANDISE_OUT_OF_STOCK_CODE)
      ) {
        return { success: false, error: e };
      }
      console.error(e);
      attempts += 1;
    }
  }

  console.error("failed to add redo product to cart");
  return {
    success: false,
    error: new Error("failed to add redo product to cart"),
  };
}

export async function safeUpdateCartAttribute({
  client,
  attributes,
  retries = 0,
}: {
  client: ExtensionShopifyClient;
  attributes: ShopifyCartAttributes;
  retries?: number;
}) {
  let attempts = 0;

  while (attempts <= retries) {
    try {
      return await client.updateCartAttribute(attributes);
    } catch (e) {
      console.error(e);
      attempts += 1;
    }
  }

  console.error("failed to add opted in cart attribute");
  return;
}

export async function setInitialCartAttributes(
  client: ExtensionShopifyClient,
  selectedTreatment: Treatment | undefined,
  autocheck: WidgetAutocheck,
) {
  let cart = await client.cartGet();

  const cartAttrUpdates: { key: string; value: string }[] = [];

  if (selectedTreatment) {
    if (cart.attributes?.redo_treatment_id !== selectedTreatment._id) {
      cartAttrUpdates.push({
        key: "redo_treatment_id",
        value: selectedTreatment._id,
      });
    }
  } else if (cart.attributes?.redo_treatment_id) {
    cartAttrUpdates.push({ key: "redo_treatment_id", value: "" });
  }

  const shouldMarkRedoLoadedOnCart =
    autocheck.usingCartAndCheckoutToggle ||
    isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy];

  if (
    shouldMarkRedoLoadedOnCart &&
    !cart.attributes?.[REDO_LOADED_ON_CART_ATTRIBUTE_KEY]
  ) {
    cartAttrUpdates.push({
      key: REDO_LOADED_ON_CART_ATTRIBUTE_KEY,
      value: "true",
    });
  }

  if (
    cart.attributes?.redo_opted_in_from_cart &&
    !autocheck.setCheckoutToggleBasedOnOptInCartAttribute
  ) {
    cartAttrUpdates.push({
      key: REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY,
      value: "",
    });
  }

  if (cartAttrUpdates.length) {
    const updatedCart = await client.updateCartAttribute({
      attributes: cartAttrUpdates,
    });
    if (updatedCart) {
      cart = updatedCart;
    }
  }

  setSessionStorageWithExpiry(REDO_CART_TOKEN_STORAGE_KEY, cart.token, 0.15);

  return cart;
}
